import React from "react";
import { Button, FormControl, FormLabel, MenuItem, Select, Typography } from "@mui/material";
import { Box } from "@mui/system";

import { Modal } from "fond/widgets";

import { c2sFileMap } from "./files";
import { C2SUploadArtifacts } from "./UploadConfigurationStep";

interface UploadFileMappingModalProps {
  open: boolean;
  unmatchedFiles: File[];
  fileMapping: Record<string, keyof C2SUploadArtifacts | "">;
  onFileMapChange: (fileName: string, mappingType: keyof C2SUploadArtifacts | "") => void;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Modal component for mapping unmatched files to their intended configuration types.
 */
export const UploadFileMappingModal: React.FC<UploadFileMappingModalProps> = ({
  open,
  unmatchedFiles,
  fileMapping,
  onFileMapChange,
  onConfirm,
  onCancel,
}) => {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      header="Map Unmatched Files"
      content={
        <>
          <Typography variant="body2" sx={{ mb: 3 }}>
            The following files could not be automatically matched. Please specify the intended configuration for each file:
          </Typography>
          {unmatchedFiles.map((file) => {
            // Get all selected file types except the current file's selection
            const selectedTypes = Object.entries(fileMapping)
              .filter(([fileName, type]) => fileName !== file.name && type !== "")
              .map(([, type]) => type);

            // Get file extension to determine valid mappings
            const lastDotIndex = file.name.lastIndexOf(".");
            const fileExtension = lastDotIndex === -1 ? "" : file.name.slice(lastDotIndex).toLowerCase();
            const isJson = fileExtension === ".json";
            const isCsv = fileExtension === ".csv";
            const hasKnownExtension = isJson || isCsv;

            return (
              <Box key={file.name} sx={{ mb: 2 }}>
                <FormLabel sx={{ mb: 1, display: "block" }}>{file.name}</FormLabel>
                <FormControl fullWidth size="small">
                  <Select
                    value={fileMapping[file.name] || ""}
                    onChange={(e) => onFileMapChange(file.name, e.target.value as keyof C2SUploadArtifacts | "")}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>Please select...</em>
                    </MenuItem>
                    {c2sFileMap.ArtifactFiles.map(({ id, title }) => {
                      // Disable if already selected by another file
                      const alreadySelected = selectedTypes.includes(id);

                      // Disable if file extension doesn't match the expected type (only when extension is known)
                      const invalidExtension =
                        hasKnownExtension && ((id === "layersConfiguration" && !isJson) || ((id === "costBreakdown" || id === "bom") && !isCsv));

                      return (
                        <MenuItem key={id} value={id} disabled={alreadySelected || invalidExtension}>
                          {title}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>
            );
          })}
        </>
      }
      actions={
        <>
          <Button onClick={onCancel}>Cancel</Button>
          <Button onClick={onConfirm} variant="contained" disabled={unmatchedFiles.some((file) => !fileMapping[file.name])}>
            Confirm
          </Button>
        </>
      }
    />
  );
};
